import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Plus,
  Minus,
  X,
  Copy,
  Mail,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import Sidebar from '../../components/ui/Sidebar';
import { useToast } from '../../components/ui/Toast';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import useCheckoutLinkActions from './useCheckoutLinkActions';
import WhatsAppIcon from './WhatsAppIcon';
import './SendCheckoutLinkSidebar.css';

/* ─── Module-scope sub-components ───────────────────────────────
   Hoisted out of the parent component so React doesn't unmount +
   remount their subtrees on every parent re-render — that's the
   input-focus-loss trap from feedback_no_inline_components.md.    */

function Stepper({ step }) {
  return (
    <div className="sl-send-stepper" aria-hidden>
      <div className={`sl-send-stepper-bar ${step === 1 ? 'is-current' : 'is-done'}`} />
      <div className={`sl-send-stepper-bar ${step === 2 ? 'is-current' : ''}`} />
    </div>
  );
}

/**
 * Flatten a product into one row per sellable unit. A product with no
 * variations becomes a single base row; a product with the variant-matrix
 * format becomes one row per variant (using each variant's price + stock).
 * Returns: { key, productId, variantId?, label, sublabel, price, stock, image }
 */
function expandProductRows(p) {
  if (!p) return [];
  const baseImage = p.primaryImage || null;
  // New variant-matrix format: product.variations = { attributes, variants }
  // Legacy format: product.variations = [{ name, values }] (no per-variant
  // stock/price) — we treat those products as single-row (base only).
  const matrix =
    p.variations &&
    typeof p.variations === 'object' &&
    !Array.isArray(p.variations) &&
    Array.isArray(p.variations.variants)
      ? p.variations.variants
      : null;

  if (p.hasVariations && matrix && matrix.length > 0) {
    return matrix
      .filter((v) => v && v.id)
      .map((v) => {
        const combinationLabel =
          v.combination && typeof v.combination === 'object'
            ? Object.entries(v.combination)
                .map(([, val]) => val)
                .join(' / ')
            : v.name || '';
        return {
          key: `${p.id}:${v.id}`,
          productId: p.id,
          variantId: v.id,
          label: p.name,
          sublabel: combinationLabel || 'Variant',
          price: parseFloat(v.price ?? p.price) || 0,
          stock:
            v.quantity != null ? Number(v.quantity) : Number(p.quantity ?? 0),
          image: baseImage,
        };
      });
  }

  return [
    {
      key: p.id,
      productId: p.id,
      variantId: null,
      label: p.name,
      sublabel: null,
      price: parseFloat(p.price) || 0,
      stock: Number(p.quantity ?? 0),
      image: baseImage,
    },
  ];
}

function ProductPicker({ query, setQuery, products, loading, onAdd, formatMoney }) {
  // Each product expands into 1+ rows (one per variant). Search matches
  // either the product name, variant label, or SKU.
  const filtered = useMemo(() => {
    const allRows = products.flatMap(expandProductRows);
    if (!query.trim()) return allRows.slice(0, 50);
    const q = query.trim().toLowerCase();
    return allRows
      .filter((row) => {
        if ((row.label || '').toLowerCase().includes(q)) return true;
        if ((row.sublabel || '').toLowerCase().includes(q)) return true;
        return false;
      })
      .slice(0, 50);
  }, [query, products]);

  return (
    <div className="sl-send-picker">
      <label className="sl-send-picker-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search products or variants"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="sl-send-picker-input"
        />
      </label>

      {loading && <p className="sl-send-picker-empty">Loading products…</p>}

      {!loading && filtered.length === 0 && (
        <p className="sl-send-picker-empty">
          {query
            ? `No products match "${query}".`
            : 'You have no active products yet. Add one in Products first.'}
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="sl-send-picker-list">
          {filtered.map((row) => (
            <button
              key={row.key}
              type="button"
              className="sl-send-picker-row"
              onClick={() => onAdd(row)}
              disabled={row.stock <= 0}
            >
              <div className="sl-send-picker-thumb">
                {row.image ? (
                  <img src={row.image} alt="" />
                ) : (
                  <span aria-hidden>—</span>
                )}
              </div>
              <div className="sl-send-picker-meta">
                <p className="sl-send-picker-name">
                  {row.label}
                  {row.sublabel && (
                    <span className="sl-send-picker-variant"> · {row.sublabel}</span>
                  )}
                </p>
                <p className="sl-send-picker-sub">
                  {formatMoney(row.price)} ·{' '}
                  {row.stock > 0 ? `${row.stock} in stock` : 'Out of stock'}
                </p>
              </div>
              <Plus size={16} className="sl-send-picker-add" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SelectedCartList({ items, onChangeQty, onRemove, formatMoney }) {
  if (items.length === 0) {
    return <p className="sl-send-cart-empty">No items added yet.</p>;
  }
  return (
    <div className="sl-send-cart">
      {items.map((item) => {
        const atMax = item.maxQty != null && item.qty >= item.maxQty;
        return (
          <div key={item.key} className="sl-send-cart-row">
            <div className="sl-send-cart-info">
              <p className="sl-send-cart-name">{item.name}</p>
              <p className="sl-send-cart-price">
                {formatMoney(item.price)} each
                {item.maxQty != null && (
                  <span className="sl-send-cart-stock">
                    {' '}· {item.maxQty} in stock
                  </span>
                )}
              </p>
            </div>
            <div className="sl-send-cart-qty">
              <button
                type="button"
                className="sl-send-qty-btn"
                onClick={() => onChangeQty(item.key, Math.max(item.qty - 1, 1))}
                disabled={item.qty <= 1}
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span className="sl-send-qty-val">{item.qty}</span>
              <button
                type="button"
                className="sl-send-qty-btn"
                onClick={() => onChangeQty(item.key, item.qty + 1)}
                disabled={atMax}
                aria-label="Increase quantity"
                title={atMax ? 'Reached available stock' : undefined}
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              type="button"
              className="sl-send-cart-remove"
              onClick={() => onRemove(item.key)}
              aria-label={`Remove ${item.name}`}
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function TotalsCard({ items, formatMoney }) {
  const subtotal = items.reduce(
    (s, it) => s + Number(it.price) * Number(it.qty || 0),
    0
  );
  return (
    <div className="sl-send-totals">
      <span className="sl-send-totals-label">Cart total</span>
      <span className="sl-send-totals-value">{formatMoney(subtotal)}</span>
    </div>
  );
}

function CustomerSettingsForm({ values, onChange, advancedOpen, setAdvancedOpen }) {
  return (
    <div className="sl-send-form">
      <label className="sl-send-field">
        <span className="sl-send-field-label">Customer name</span>
        <input
          className="sl-send-field-input"
          type="text"
          value={values.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Jane Doe"
        />
      </label>

      <label className="sl-send-field">
        <span className="sl-send-field-label">Email <span className="sl-send-field-hint">(needed to email link)</span></span>
        <input
          className="sl-send-field-input"
          type="email"
          value={values.email}
          onChange={(e) => onChange({ email: e.target.value })}
          placeholder="jane@example.com"
        />
      </label>

      <label className="sl-send-field">
        <span className="sl-send-field-label">Phone <span className="sl-send-field-hint">(needed for WhatsApp send)</span></span>
        <input
          className="sl-send-field-input"
          type="tel"
          value={values.phone}
          onChange={(e) => onChange({ phone: e.target.value })}
          placeholder="+234 803 000 0000"
        />
      </label>

      <label className="sl-send-field">
        <span className="sl-send-field-label">Note <span className="sl-send-field-hint">(internal — won&apos;t be shown)</span></span>
        <textarea
          className="sl-send-field-input sl-send-field-textarea"
          rows={2}
          value={values.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="First-time customer from WhatsApp"
        />
      </label>

      <button
        type="button"
        className="sl-send-advanced-toggle"
        onClick={() => setAdvancedOpen((v) => !v)}
        aria-expanded={advancedOpen}
      >
        <span>{advancedOpen ? 'Hide advanced settings' : 'Advanced settings'}</span>
        {advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {advancedOpen && (
        <div className="sl-send-advanced">
          <label className="sl-send-field">
            <span className="sl-send-field-label">Expires in</span>
            <div className="sl-send-expiry">
              <input
                className="sl-send-field-input sl-send-expiry-input"
                type="number"
                min="1"
                max="30"
                value={values.expiresInDays}
                onChange={(e) =>
                  onChange({ expiresInDays: Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 7)) })
                }
              />
              <span className="sl-send-expiry-unit">days</span>
            </div>
            <p className="sl-send-field-hint">Default 7 days, max 30.</p>
          </label>
        </div>
      )}
    </div>
  );
}

function SentState({ link, customerInfo, onCopy, onEmail, onWhatsApp }) {
  const hasEmail = !!customerInfo.email;
  const hasPhone = !!customerInfo.phone;
  return (
    <div className="sl-send-sent">
      <div className="sl-send-sent-head">
        <CheckCircle2 size={26} className="sl-send-sent-icon" />
        <h3 className="sl-send-sent-title">Link ready to share</h3>
        <p className="sl-send-sent-sub">
          Pick how you want to send it. You can come back to this link any time from the
          Checkout links tab.
        </p>
      </div>

      <div className="sl-send-url">
        <span className="sl-send-url-text" title={link.url}>{link.url}</span>
        <button
          type="button"
          className="sl-send-url-copy"
          onClick={onCopy}
          aria-label="Copy URL"
        >
          <Copy size={14} />
        </button>
      </div>

      <div className="sl-send-actions">
        <button
          type="button"
          className="sl-send-action sl-send-action--primary"
          onClick={onCopy}
        >
          <Copy size={15} />
          <span>Copy</span>
        </button>
        <button
          type="button"
          className="sl-send-action"
          onClick={onEmail}
          disabled={!hasEmail}
          title={!hasEmail ? 'Add a customer email to enable' : undefined}
        >
          <Mail size={15} />
          <span>Email</span>
        </button>
        <button
          type="button"
          className="sl-send-action sl-send-action--whatsapp"
          onClick={onWhatsApp}
          disabled={!hasPhone}
          title={!hasPhone ? 'Add a customer phone to enable' : undefined}
        >
          <WhatsAppIcon size={16} />
          <span>WhatsApp</span>
        </button>
      </div>
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────── */

function buildWhatsAppUrl(phone, url, storeName) {
  // Strip everything that isn't a digit. wa.me requires E.164-ish digits
  // with no leading + and no other punctuation.
  const digits = String(phone || '').replace(/\D+/g, '');
  if (!digits) return null;
  const greeting = storeName ? `${storeName} sent you a checkout link: ` : 'Your checkout link: ';
  return `https://wa.me/${digits}?text=${encodeURIComponent(greeting + url)}`;
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function SendCheckoutLinkSidebar({ isOpen, onClose, onCreated, storeName }) {
  const toast = useToast();
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const { getAxios } = useSecureAuth();
  const { createLink, emailLink } = useCheckoutLinkActions();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [productQuery, setProductQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  // Each selected entry is keyed by productId or productId:variantId so the
  // same product with two different variants stays separate. maxQty caps the
  // qty stepper at the available stock at picker-time; the backend will
  // re-check at link-creation time and refuse if anything dropped to zero.
  const [selected, setSelected] = useState([]); // [{ key, productId, variantId, name, price, qty, maxQty }]

  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    expiresInDays: 7,
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [createdLink, setCreatedLink] = useState(null);

  const formatMoney = useCallback(
    (n) => formatInDisplayCurrency(Number(n || 0), accountCurrency),
    [formatInDisplayCurrency, accountCurrency]
  );

  // Fetch products on open. Reset state when closed.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setProductsLoading(true);
    (async () => {
      try {
        const axios = getAxios();
        const resp = await axios.get('/merchants/products', { params: { limit: 200 } });
        const list = resp?.data?.products || resp?.data || [];
        if (!cancelled) {
          setProducts(
            Array.isArray(list)
              ? list.filter((p) => p && p.status !== 'DRAFT' && p.deletedAt == null)
              : []
          );
        }
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, getAxios]);

  const resetAll = useCallback(() => {
    setStep(1);
    setProductQuery('');
    setSelected([]);
    setCustomer({ name: '', email: '', phone: '', notes: '', expiresInDays: 7 });
    setAdvancedOpen(false);
    setCreatedLink(null);
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    // Defer reset so the close animation can finish before state flush.
    setTimeout(resetAll, 200);
  }, [onClose, resetAll]);

  const addProduct = useCallback((row) => {
    setSelected((prev) => {
      const existing = prev.find((i) => i.key === row.key);
      if (existing) {
        // Don't go past the stock cap on bump-up.
        const nextQty =
          existing.maxQty != null
            ? Math.min(existing.qty + 1, existing.maxQty)
            : existing.qty + 1;
        return prev.map((i) =>
          i.key === row.key ? { ...i, qty: nextQty } : i
        );
      }
      return [
        ...prev,
        {
          key: row.key,
          productId: row.productId,
          variantId: row.variantId || null,
          name: row.sublabel ? `${row.label} — ${row.sublabel}` : row.label,
          price: row.price,
          qty: 1,
          maxQty: row.stock,
        },
      ];
    });
  }, []);

  const changeQty = useCallback((key, qty) => {
    setSelected((prev) =>
      prev.map((i) => {
        if (i.key !== key) return i;
        const capped =
          i.maxQty != null ? Math.min(Math.max(qty, 1), i.maxQty) : Math.max(qty, 1);
        return { ...i, qty: capped };
      })
    );
  }, []);

  const removeItem = useCallback((key) => {
    setSelected((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const handleGenerate = useCallback(async () => {
    if (selected.length === 0) {
      toast.error('Add at least one product');
      return;
    }
    setSubmitting(true);
    const link = await createLink({
      cart: selected.map((i) => ({
        productId: i.productId,
        variantId: i.variantId || undefined,
        qty: i.qty,
      })),
      customerInfo: customer.name || customer.email || customer.phone
        ? {
            name: customer.name || null,
            email: customer.email || null,
            phone: customer.phone || null,
          }
        : null,
      notes: customer.notes || null,
      expiresInDays: customer.expiresInDays,
    });
    setSubmitting(false);
    if (link) {
      setCreatedLink(link);
      setStep(3); // sent state
      if (typeof onCreated === 'function') {
        onCreated(link);
      }
    }
  }, [createLink, selected, customer, toast, onCreated]);

  const handleCopy = useCallback(async () => {
    if (!createdLink?.url) return;
    try {
      await navigator.clipboard.writeText(createdLink.url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  }, [createdLink, toast]);

  const handleEmail = useCallback(async () => {
    if (!createdLink || !customer.email) return;
    await emailLink(createdLink.id, customer.email);
  }, [createdLink, customer.email, emailLink]);

  const handleWhatsApp = useCallback(() => {
    if (!createdLink || !customer.phone) return;
    const waUrl = buildWhatsAppUrl(customer.phone, createdLink.url, storeName);
    if (waUrl) {
      window.open(waUrl, '_blank', 'noopener,noreferrer');
      toast.success('Opening WhatsApp…');
    } else {
      toast.error('That phone number doesn’t look valid');
    }
  }, [createdLink, customer.phone, storeName, toast]);

  /* ─── Footer slot per step ──────────────────────────────────── */

  let footer = null;
  if (step === 1) {
    footer = (
      <div className="sl-send-footer-row">
        <button
          type="button"
          className="sl-send-footer-btn sl-send-footer-btn--ghost"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="sl-send-footer-btn sl-send-footer-btn--primary"
          onClick={() => setStep(2)}
          disabled={selected.length === 0}
        >
          Next
        </button>
      </div>
    );
  } else if (step === 2) {
    footer = (
      <div className="sl-send-footer-row">
        <button
          type="button"
          className="sl-send-footer-btn sl-send-footer-btn--ghost"
          onClick={() => setStep(1)}
        >
          Back
        </button>
        <button
          type="button"
          className="sl-send-footer-btn sl-send-footer-btn--primary"
          onClick={handleGenerate}
          disabled={submitting || selected.length === 0}
        >
          {submitting ? 'Generating…' : 'Generate link'}
        </button>
      </div>
    );
  } else if (step === 3) {
    footer = (
      <div className="sl-send-footer-row">
        <button
          type="button"
          className="sl-send-footer-btn sl-send-footer-btn--primary"
          onClick={handleClose}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={handleClose}
      title="Send a checkout link"
      subtitle={
        step === 3
          ? null
          : 'Build a cart for any customer, then share it via copy, email, or WhatsApp.'
      }
      width="lg"
      footer={footer}
    >
      {step !== 3 && <Stepper step={step} />}

      {step === 1 && (
        <div className="sl-send-step">
          <h3 className="sl-send-step-title">1. Build the cart</h3>
          <ProductPicker
            query={productQuery}
            setQuery={setProductQuery}
            products={products}
            loading={productsLoading}
            onAdd={addProduct}
            formatMoney={formatMoney}
          />

          <div className="sl-send-divider" />

          <h4 className="sl-send-substep">Selected items</h4>
          <SelectedCartList
            items={selected}
            onChangeQty={changeQty}
            onRemove={removeItem}
            formatMoney={formatMoney}
          />
          {selected.length > 0 && (
            <TotalsCard items={selected} formatMoney={formatMoney} />
          )}
        </div>
      )}

      {step === 2 && (
        <div className="sl-send-step">
          <h3 className="sl-send-step-title">2. Customer &amp; settings</h3>
          <p className="sl-send-step-sub">
            Email and phone are optional — fill in either to enable that send option after
            the link is generated.
          </p>
          <CustomerSettingsForm
            values={customer}
            onChange={(patch) => setCustomer((prev) => ({ ...prev, ...patch }))}
            advancedOpen={advancedOpen}
            setAdvancedOpen={setAdvancedOpen}
          />

          <div className="sl-send-recap" role="status" aria-live="polite">
            <AlertCircle size={14} />
            <span>
              {selected.length} item{selected.length === 1 ? '' : 's'} · expires in{' '}
              {customer.expiresInDays} day{customer.expiresInDays === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      )}

      {step === 3 && createdLink && (
        <div className="sl-send-step">
          <SentState
            link={createdLink}
            customerInfo={customer}
            onCopy={handleCopy}
            onEmail={handleEmail}
            onWhatsApp={handleWhatsApp}
            onDone={handleClose}
          />
        </div>
      )}
    </Sidebar>
  );
}
